// utils/validateBOM.js

/**
 * Validate BOM structure for saving
 * - All nodes must be connected as one tree
 * - No multiple disconnected trees
 * - No cycles or self-references
 * - Valid quantities
 */
export const validateBOMConnectivity = (nodes, edges) => {
  const errors = [];
  const warnings = [];

  if (nodes.length === 0) {
    errors.push('No items on canvas');
    return { isValid: false, errors, warnings, orphanNodes: [], cycleNodes: [], topLevelItems: [] };
  }

  // If only one node and no edges, that's fine (single item BOM with no components)
  if (nodes.length === 1 && edges.length === 0) {
    return {
      isValid: true,
      errors: [],
      warnings: ['Single item with no components'],
      orphanNodes: [],
      cycleNodes: [],
      topLevelItems: [nodes[0]],
    };
  }

  // Build adjacency lists
  // In our edge structure: source = child, target = parent
  const childToParents = new Map();
  const parentToChildren = new Map();

  edges.forEach(edge => {
    if (!childToParents.has(edge.source)) {
      childToParents.set(edge.source, new Set());
    }
    childToParents.get(edge.source).add(edge.target);

    if (!parentToChildren.has(edge.target)) {
      parentToChildren.set(edge.target, new Set());
    }
    parentToChildren.get(edge.target).add(edge.source);
  });

  // Find top-level items (nodes that have no parent - never appear as edge.source)
  const childNodeIds = new Set(edges.map(e => e.source));
  const topLevelItems = nodes.filter(n => !childNodeIds.has(n.id));

  // Check for self-references
  edges.forEach(edge => {
    if (edge.source === edge.target) {
      errors.push('Self-reference detected');
    }
  });

  // Check for cycles
  const cycleNodes = detectCycles(nodes, edges);
  if (cycleNodes.length > 0) {
    errors.push('Circular reference detected');
  }

  // Check for invalid quantities
  let hasInvalidQty = false;
  edges.forEach(edge => {
    const qty = edge.data?.quantity;
    if (qty === undefined || qty === null || qty <= 0) {
      hasInvalidQty = true;
    }
  });
  if (hasInvalidQty) {
    warnings.push('Some connections have invalid quantities (must be > 0)');
  }

  // Find all connected components
  const visited = new Set();
  const components = [];

  const bfs = (startNodeId) => {
    const component = new Set();
    const queue = [startNodeId];

    while (queue.length > 0) {
      const current = queue.shift();
      if (visited.has(current)) continue;
      
      visited.add(current);
      component.add(current);

      // Get connected nodes (both directions)
      const parents = childToParents.get(current) || new Set();
      const children = parentToChildren.get(current) || new Set();

      parents.forEach(parentId => {
        if (!visited.has(parentId)) queue.push(parentId);
      });
      children.forEach(childId => {
        if (!visited.has(childId)) queue.push(childId);
      });
    }

    return component;
  };

  // Find all connected components
  nodes.forEach(node => {
    if (!visited.has(node.id)) {
      const component = bfs(node.id);
      components.push(component);
    }
  });

  // Check for orphan nodes (nodes with no connections at all)
  const nodesWithConnections = new Set();
  edges.forEach(edge => {
    nodesWithConnections.add(edge.source);
    nodesWithConnections.add(edge.target);
  });

  const orphanNodes = nodes.filter(n => !nodesWithConnections.has(n.id)).map(n => ({
    id: n.id,
    itemCode: n.data.itemCode,
    itemName: n.data.itemName,
  }));

  // Separate components into trees (have edges) and orphans (single nodes)
  const treeComponents = components.filter(comp => {
    // A component is a tree if any of its nodes has edges
    for (const nodeId of comp) {
      if (nodesWithConnections.has(nodeId)) return true;
    }
    return false;
  });

  // Check: must have exactly one connected tree structure
  if (treeComponents.length > 1) {
    errors.push(`Multiple disconnected BOM structures found (${treeComponents.length}). All items must be connected as one tree.`);
  }

  // Check: orphan nodes not allowed when saving
  if (orphanNodes.length > 0 && edges.length > 0) {
    errors.push(`${orphanNodes.length} item(s) not connected to the BOM structure`);
  }

  // Check: if we have edges, we should have exactly one top-level item
  if (edges.length > 0 && topLevelItems.length === 0) {
    errors.push('No top-level item found (circular structure?)');
  } else if (edges.length > 0 && topLevelItems.length > 1) {
    // Multiple top-level items means either multiple trees or items need connecting
    const topLevelCodes = topLevelItems.map(n => n.data.itemCode).join(', ');
    errors.push(`Multiple top-level items found: ${topLevelCodes}. Connect them or remove extras.`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    orphanNodes,
    cycleNodes,
    topLevelItems,
  };
};

/**
 * Detect cycles in the BOM structure
 * Returns array of node IDs involved in cycles
 */
const detectCycles = (nodes, edges) => {
  const cycleNodes = [];
  const visited = new Set();
  const recursionStack = new Set();

  // Build parent -> children map
  const parentToChildren = new Map();
  edges.forEach(edge => {
    if (!parentToChildren.has(edge.target)) {
      parentToChildren.set(edge.target, []);
    }
    parentToChildren.get(edge.target).push(edge.source);
  });

  const dfs = (nodeId, path) => {
    if (recursionStack.has(nodeId)) {
      // Found a cycle
      const cycleStart = path.indexOf(nodeId);
      const cyclePath = path.slice(cycleStart);
      cyclePath.forEach(id => {
        if (!cycleNodes.includes(id)) {
          cycleNodes.push(id);
        }
      });
      return true;
    }

    if (visited.has(nodeId)) {
      return false;
    }

    visited.add(nodeId);
    recursionStack.add(nodeId);

    const children = parentToChildren.get(nodeId) || [];
    for (const childId of children) {
      if (dfs(childId, [...path, nodeId])) {
        return true;
      }
    }

    recursionStack.delete(nodeId);
    return false;
  };

  nodes.forEach(node => {
    if (!visited.has(node.id)) {
      dfs(node.id, []);
    }
  });

  return cycleNodes;
};

/**
 * Check if adding an edge would create a cycle
 */
export const wouldCreateCycle = (edges, newSourceId, newTargetId) => {
  // Build parent -> children map including the new edge
  const parentToChildren = new Map();
  
  edges.forEach(edge => {
    if (!parentToChildren.has(edge.target)) {
      parentToChildren.set(edge.target, new Set());
    }
    parentToChildren.get(edge.target).add(edge.source);
  });

  // Add the new edge
  if (!parentToChildren.has(newTargetId)) {
    parentToChildren.set(newTargetId, new Set());
  }
  parentToChildren.get(newTargetId).add(newSourceId);

  // Check if we can reach newTargetId from newSourceId (would be a cycle)
  const visited = new Set();
  const queue = [newSourceId];

  while (queue.length > 0) {
    const current = queue.shift();
    
    if (current === newTargetId) {
      return true; // Cycle detected
    }

    if (visited.has(current)) {
      continue;
    }
    visited.add(current);

    const children = parentToChildren.get(current) || new Set();
    children.forEach(childId => {
      if (!visited.has(childId)) {
        queue.push(childId);
      }
    });
  }

  return false;
};

/**
 * Get the top-level item(s) from a node/edge structure
 * Top-level = nodes that have children but no parent
 */
export const getTopLevelItems = (nodes, edges) => {
  if (nodes.length === 0) return [];
  if (edges.length === 0) return nodes; // All nodes are top-level if no edges
  
  const childNodeIds = new Set(edges.map(e => e.source));
  return nodes.filter(n => !childNodeIds.has(n.id));
};